import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCurrentLanguage,
  getCurrentTranslations,
  setLanguage as saveLanguage,
} from '../i18n/i18n';
import {
  getLanguageFromPath,
  getLanguagePath,
  updateMetaTags,
  updatePageTitle,
  updateStructuredData,
} from '../utils/seo';
import {
  getQuestionHeading,
  getSeoTitle,
} from '../seo/contentMetadata';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguageState] = useState(getCurrentLanguage());
  const [translations, setTranslations] = useState(getCurrentTranslations());
  const [forceUpdate, setForceUpdate] = useState(0);

  const applySeo = (nextLanguage) => {
    updatePageTitle(nextLanguage);
    updateMetaTags(nextLanguage);
    updateStructuredData(nextLanguage);
    document.title = getSeoTitle(nextLanguage);
  };

  useEffect(() => {
    const pathLanguage = getLanguageFromPath(location.pathname);

    if (!pathLanguage) {
      const currentPath = getLanguagePath(language);
      navigate(`/${currentPath}`, { replace: true });
      applySeo(language);
      return;
    }

    if (pathLanguage !== language) {
      saveLanguage(pathLanguage);
      setLanguageState(pathLanguage);
      setTranslations(getCurrentTranslations());
    }

    applySeo(pathLanguage);
  }, [location.pathname]);

  const changeLanguage = (newLanguage) => {
    if (!saveLanguage(newLanguage)) {
      return false;
    }

    setLanguageState(newLanguage);
    setTranslations(getCurrentTranslations());
    setForceUpdate((previous) => previous + 1);
    applySeo(newLanguage);

    const newPath = getLanguagePath(newLanguage);
    navigate(`/${newPath}`, { replace: true });

    window.dispatchEvent(
      new CustomEvent('languagechange', {
        detail: { language: newLanguage },
      }),
    );

    return true;
  };

  const t = (key, fallback) => {
    const questionHeading = getQuestionHeading(key, language);

    if (questionHeading) {
      return questionHeading;
    }

    const keys = key.split('.');
    let value = translations;

    for (const currentKey of keys) {
      if (
        value
        && typeof value === 'object'
        && currentKey in value
      ) {
        value = value[currentKey];
      } else {
        return fallback || key;
      }
    }

    return value || fallback || key;
  };

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || getCurrentLanguage();
      setLanguageState(newLanguage);
      setTranslations(getCurrentTranslations());
      setForceUpdate((previous) => previous + 1);
      applySeo(newLanguage);
    };

    window.addEventListener('languagechange', handleLanguageChange);

    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }, []);

  const value = {
    language,
    changeLanguage,
    t,
    translations,
    forceUpdate,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};